"use client";

import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import { groupChatsByDate } from "@/lib/chatUtils";
import type { Chat } from "./types";

type SidebarState =
  | "expanded"
  | "collapsed"
  | "transition-opening"
  | "transition-closing";

interface ChatListProps {
  chats: Chat[];
  activeChat: string | null;
  isCollapsed: boolean;
  sidebarState: SidebarState;
  onSelectChat: (chatId: string) => void;
  onDeleteChat: (chatId: string) => void;
}

export function ChatList({
  chats,
  activeChat,
  isCollapsed,
  sidebarState,
  onSelectChat,
  onDeleteChat,
}: ChatListProps) {
  const chatGroups = groupChatsByDate(chats);

  // Determine when to show expanded chat list
  const showExpandedList =
    sidebarState === "expanded" || sidebarState === "transition-opening";

  // Different animation timings for opening vs closing
  const listDelay = sidebarState === "transition-opening" ? 0.1 : 0;
  const listDuration = sidebarState === "transition-closing" ? 0 : 0.15;

  return (
    <div className="flex-1 overflow-y-auto shrink-0">
      {!isCollapsed && (
        /* Expanded chat list */
        <AnimatePresence>
          {showExpandedList && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0, transition: { duration: listDuration } }}
              transition={{ duration: 0.15, delay: listDelay }}
              className="px-3 pb-16"
            >
              <div className="space-y-3 pb-3">
                {chatGroups.map((group) => (
                  <div key={group.label}>
                    <div className="px-3 pb-1 text-xs font-medium text-muted-foreground/70 uppercase tracking-wider">
                      {group.label}
                    </div>
                    <div className="space-y-1">
                      {group.chats.map((chat) => (
                        <div key={chat.id} className="relative">
                          <button
                            onClick={() => onSelectChat(chat.id)}
                            className={cn(
                              "group relative w-full rounded-lg px-3 py-2 text-left transition-all",
                              activeChat === chat.id
                                ? "bg-(--viewport-bg)"
                                : "hover:text-primary-600"
                            )}
                          >
                            <p
                              className={cn(
                                "text-sm truncate",
                                activeChat === chat.id
                                  ? "text-primary-600 font-medium"
                                  : "text-primary-600"
                              )}
                            >
                              {chat.title}
                            </p>
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onDeleteChat(chat.id);
                            }}
                            className="absolute top-1.5 right-2 opacity-0 hover:opacity-100 group-hover:opacity-60 group-hover:hover:opacity-100 p-1 hover:bg-destructive/10 rounded-sm transition-all z-10"
                          >
                            <X className="h-3 w-3 text-muted-foreground hover:text-destructive" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      )}
    </div>
  );
}
